import { useState } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Sparkle,
  Backpack,
  Info,
  Sun,
  SunHorizon,
  CloudMoon,
  Moon,
  Tree,
  House,
} from "@phosphor-icons/react";

const EXAMPLE_KEYS = ["ai_example_1", "ai_example_2", "ai_example_3", "ai_example_4"];

const TIME_CHIPS = [
  { v: "sunrise", labelKey: "tod_sunrise", Icon: SunHorizon },
  { v: "morning", labelKey: "tod_morning", Icon: Sun },
  { v: "noon", labelKey: "tod_midday", Icon: Sun },
  { v: "afternoon", labelKey: "tod_afternoon", Icon: Sun },
  { v: "sunset", labelKey: "tod_golden", Icon: SunHorizon },
  { v: "blue_hour", labelKey: "tod_blue", Icon: CloudMoon },
  { v: "night", labelKey: "tod_night", Icon: Moon },
];

const SETTING_CHIPS = [
  { v: "outdoor", labelKey: "set_outdoor", Icon: Tree },
  { v: "indoor", labelKey: "set_indoor", Icon: House },
];

function Chip({ active, onClick, Icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs border flex items-center gap-1.5 transition-all duration-200 ${
        active
          ? "bg-[#F5A524] text-[#0A0A0A] border-[#F5A524] font-medium"
          : "bg-[#0A0A0A] text-[#A1A1AA] border-white/10 hover:border-[#F5A524]/50 hover:text-white"
      }`}
      data-testid={testId}
    >
      <Icon weight={active ? "fill" : "regular"} className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

export default function AIShotForm({ onSubmit, loading, hasGear }) {
  const { t } = useTranslation();
  const [description, setDescription] = useState("");
  const [timeOfDay, setTimeOfDay] = useState(null);
  const [setting, setSetting] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(description, timeOfDay, setting);
  };

  const toggle = (current, value, setter) => setter(current === value ? null : value);

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="ai-shot-form">
      <div className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/20 rounded-xl p-5 space-y-4">
        <div className="space-y-1">
          <h3 className="text-white font-['Outfit'] font-medium flex items-center gap-2 text-sm">
            <Sparkle weight="fill" className="text-[#F5A524] w-4 h-4" />
            {t("ai_title")}
          </h3>
          <p className="text-[#71717A] text-xs leading-relaxed">{t("ai_subtitle")}</p>
        </div>

        <div className="space-y-1.5">
          <Label className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("ai_textarea_label")}
          </Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("ai_placeholder")}
            maxLength={1000}
            rows={4}
            className="bg-[#0A0A0A] border-white/10 text-white focus-visible:ring-[#F5A524] focus-visible:border-[#F5A524] resize-none placeholder:text-[#52525B]"
            data-testid="ai-shot-textarea"
          />
        </div>

        {/* Optional time of day */}
        <div className="space-y-2">
          <span className="text-[#71717A] text-xs uppercase tracking-[0.15em]">{t("ai_tod_label")}</span>
          <div className="flex gap-2 flex-wrap" data-testid="ai-tod-chips">
            {TIME_CHIPS.map((chip) => (
              <Chip
                key={chip.v}
                active={timeOfDay === chip.v}
                onClick={() => toggle(timeOfDay, chip.v, setTimeOfDay)}
                Icon={chip.Icon}
                label={t(chip.labelKey)}
                testId={`ai-tod-${chip.v}`}
              />
            ))}
          </div>
        </div>

        {/* Optional indoor/outdoor */}
        <div className="space-y-2">
          <span className="text-[#71717A] text-xs uppercase tracking-[0.15em]">{t("ai_setting_label")}</span>
          <div className="flex gap-2 flex-wrap" data-testid="ai-setting-chips">
            {SETTING_CHIPS.map((chip) => (
              <Chip
                key={chip.v}
                active={setting === chip.v}
                onClick={() => toggle(setting, chip.v, setSetting)}
                Icon={chip.Icon}
                label={t(chip.labelKey)}
                testId={`ai-setting-${chip.v}`}
              />
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <span className="text-[#71717A] text-xs uppercase tracking-[0.15em]">
            {t("ai_examples_label")}
          </span>
          <div className="flex gap-2 flex-wrap">
            {EXAMPLE_KEYS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setDescription(t(k))}
                className="px-3 py-1.5 rounded-lg text-xs border bg-[#0A0A0A] text-[#A1A1AA] border-white/10 hover:border-[#F5A524]/50 hover:text-white transition-all duration-200 text-left"
                data-testid={`ai-example-${k}`}
              >
                {t(k)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-start gap-2 text-xs text-[#71717A]">
          {hasGear ? (
            <>
              <Backpack weight="regular" className="text-[#F5A524] w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{t("ai_gear_hint")}</span>
            </>
          ) : (
            <>
              <Info weight="regular" className="text-[#71717A] w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{t("ai_gear_hint_none")}</span>
            </>
          )}
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-12 bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-semibold text-base active:scale-[0.98] transition-all"
          data-testid="ai-shot-submit-button"
        >
          {loading ? (
            t("ai_generating")
          ) : (
            <span className="flex items-center gap-2">
              <Sparkle weight="fill" className="w-4 h-4" />
              {t("ai_submit")}
            </span>
          )}
        </Button>
      </div>
    </form>
  );
}
